import React, { useMemo } from 'react';

export interface SessionRosterOverviewEntry {
  position: number;
  rosterId?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  seatId?: string | null;
  isUnmapped?: boolean;
}

interface SessionRosterOverviewListProps {
  entries: SessionRosterOverviewEntry[];
  getSeatLabel: (position: number) => string;
}

const RosterLine: React.FC<{
  entry: SessionRosterOverviewEntry;
  getSeatLabel: (position: number) => string;
}> = ({ entry, getSeatLabel }) => {
  const seatLabel = getSeatLabel(entry.position);
  const isUnmapped = entry.isUnmapped;

  return (
    <li className="flex items-center gap-3 py-1.5 font-sans">
      <span className="w-32 shrink-0 text-xs font-medium text-rowing-gray-500 lg:w-36">
        {seatLabel}
      </span>
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        {isUnmapped ? (
          <>
            <span className="text-xs italic text-rowing-gray-400">Unassigned</span>
            <span className="shrink-0 rounded-full border border-amber-100 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              Incomplete
            </span>
          </>
        ) : entry.displayName ? (
          <span className="truncate text-xs font-semibold text-rowing-gray-900">
            {entry.displayName}
          </span>
        ) : (
          <span className="text-xs italic text-rowing-gray-400">Unassigned</span>
        )}
      </div>
    </li>
  );
};

/**
 * Read-only session roster for the coach dashboard. Below `xl`, one full-width list
 * in seat order; at `xl+`, two balanced columns so wide screens are not one empty row.
 */
const SessionRosterOverviewList: React.FC<SessionRosterOverviewListProps> = ({
  entries,
  getSeatLabel,
}) => {
  const { left, right } = useMemo(() => {
    const mid = Math.ceil(entries.length / 2);
    return { left: entries.slice(0, mid), right: entries.slice(mid) };
  }, [entries]);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-rowing-gray-500">No roster data found for this session yet.</p>
    );
  }

  const column = (items: SessionRosterOverviewEntry[]) => (
    <ul className="min-w-0 divide-y divide-rowing-gray-100">
      {items.map((entry) => (
        <RosterLine key={entry.position} entry={entry} getSeatLabel={getSeatLabel} />
      ))}
    </ul>
  );

  return (
    <>
      <div className="xl:hidden">{column(entries)}</div>
      <div className="hidden gap-x-12 xl:grid xl:grid-cols-2 xl:items-start">
        <div className="min-w-0 border-r border-rowing-gray-100 pr-8">{column(left)}</div>
        <div className="min-w-0">{column(right)}</div>
      </div>
    </>
  );
};

export default SessionRosterOverviewList;
